import type { SignalCalc } from '../../types';

// Renders a signal's calculated Output Value from the universal contract
// (types.ts SignalCalc). Integration-agnostic on purpose: it knows display
// text, unit, period, entity, source and metadata - never what produced them.
// What a source is called ("SEC EDGAR") comes from the mapping's own
// `calc.source.label`; technical evidence stays in a collapsed section so the
// primary view is the business value, not the payload.

// "fiscalYear" -> "Fiscal year", "filed_at" -> "Filed at"
export function humanizeKey(key: string): string {
  const words = key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function sourceName(calc: SignalCalc): string | undefined {
  return calc.source?.label || (calc.source?.type ? humanizeKey(calc.source.type) : undefined);
}

function showValue(v: unknown): string {
  if (v === null || v === undefined) return '';
  return typeof v === 'object' ? JSON.stringify(v) : String(v);
}

function formatReceived(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

const NUMERIC_TYPES = new Set(['number', 'currency', 'percentage', 'count', 'duration']);

const STATUS_TEXT: Record<string, string> = {
  missing: 'No value in the source data',
  invalid: 'The value could not be read',
  ambiguous: 'More than one value matched',
  incomplete: 'Some details are missing',
};

export function SignalValueCard({ calc, name }: { calc: SignalCalc; name?: string }) {
  const v = calc.value;
  const period = calc.period;
  const title = [calc.entity?.name, calc.label || name].filter(Boolean).join(' · ');
  const unitLabel = v.type === 'currency' ? 'Currency' : v.unit ? 'Unit' : '';
  const diagnostics = calc.diagnostics ?? [];
  const problem = calc.status !== 'ok';
  const technical: [string, string][] = [
    ...Object.entries(calc.metadata ?? {}).map(([k, val]): [string, string] => [humanizeKey(k), showValue(val)]),
    ...(period?.frame ? ([['Frame', period.frame]] as [string, string][]) : []),
    ...(calc.value.text && NUMERIC_TYPES.has(v.type) && calc.value.text !== v.display ? ([['Exact value', calc.value.text]] as [string, string][]) : []),
    ...(calc.source?.url ? ([['Source URL', calc.source.url]] as [string, string][]) : []),
    ...(calc.source?.receivedAt ? ([['Received by Zmist', formatReceived(calc.source.receivedAt)]] as [string, string][]) : []),
  ].filter(([, val]) => val !== '');

  return (
    <div className="flex flex-col gap-2 rounded-xl bg-black/[0.03] dark:bg-white/[0.05] p-3" data-testid="signal-value-card">
      {title && <span className="text-sm text-black/60 dark:text-white/60">{title}</span>}
      {v.display ? (
        <span className="text-3xl font-semibold leading-tight tabular-nums">{v.display}</span>
      ) : (
        <span className="text-base font-medium text-amber-700 dark:text-amber-400">{STATUS_TEXT[calc.status] ?? 'No value'}</span>
      )}
      {period?.label && (
        <span className="flex flex-col text-sm">
          <span>{period.label}</span>
          {period.range && period.range !== period.label && <span className="text-black/50 dark:text-white/50">{period.range}</span>}
        </span>
      )}
      <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-sm">
        {(calc.label || name) && (
          <>
            <dt className="text-black/50 dark:text-white/50">Metric</dt>
            <dd className="m-0">{calc.label || name}</dd>
          </>
        )}
        {unitLabel && v.unit && (
          <>
            <dt className="text-black/50 dark:text-white/50">{unitLabel}</dt>
            <dd className="m-0">{v.unit}</dd>
          </>
        )}
        {sourceName(calc) && (
          <>
            <dt className="text-black/50 dark:text-white/50">Source</dt>
            <dd className="m-0">{sourceName(calc)}</dd>
          </>
        )}
      </dl>
      {problem && diagnostics.length > 0 && (
        <ul role="status" className="m-0 pl-4 text-xs text-amber-700 dark:text-amber-400">
          {diagnostics.map((d) => (
            <li key={`${d.code}:${d.path ?? ''}`}>{d.message}</li>
          ))}
        </ul>
      )}
      {technical.length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-black/50 dark:text-white/50">Technical details</summary>
          <dl className="m-0 mt-1.5 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 font-mono">
            {technical.map(([k, val]) => (
              <div key={k} className="contents">
                <dt className="text-black/50 dark:text-white/50">{k}</dt>
                <dd className="m-0 break-all">{val}</dd>
              </div>
            ))}
          </dl>
        </details>
      )}
    </div>
  );
}
